import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import VideoPreviousIcon from '@atlaskit/icon/core/video-previous';

import { AudioControlIconButton } from '../briefings/sub-components/AudioControlButton';

const START_SECONDS = 0;
const ZERO_EPSILON_SECONDS = 0.25;

const i18n = defineMessages({
	playFromBeginningLabel: {
		id: 'audio.miniplayer-controls.play-from-beginning-label',
		defaultMessage: 'Play from Beginning',
		description:
			'The label is used for accessibility for the icon which will be shown in the menu item that will restart the active audio from the beginning',
	},
});

export type PlayFromBeginningButtonProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
	onClick?: () => void;
	isDisabled?: boolean;
	onRestart?: () => void;
};

export const PlayFromBeginningButton: FC<PlayFromBeginningButtonProps> = ({
	audioRef,
	onClick = () => {},
	isDisabled = false,
	onRestart,
}) => {
	const intl = useIntl();

	const hasBufferedAtZero = (audio: HTMLAudioElement) => {
		try {
			const ranges = audio.buffered;
			for (let i = 0; i < ranges.length; i++) {
				const start = ranges.start(i);
				const end = ranges.end(i);
				if (start <= ZERO_EPSILON_SECONDS && end > ZERO_EPSILON_SECONDS) {
					return true;
				}
			}
		} catch {}
		return false;
	};

	const handlePlayFromBeginning = async () => {
		const audio = audioRef.current;
		if (audio) {
			if (hasBufferedAtZero(audio)) {
				audio.currentTime = START_SECONDS;
				if (audio.paused) {
					await audio.play();
				}
			} else {
				audio.pause();
				onRestart?.();
			}
		}
	};

	const handleClick = async () => {
		await handlePlayFromBeginning();
		onClick?.();
	};

	return (
		<AudioControlIconButton
			Icon={VideoPreviousIcon}
			label={intl.formatMessage(i18n.playFromBeginningLabel)}
			tooltip={intl.formatMessage(i18n.playFromBeginningLabel)}
			onClick={handleClick}
			isDisabled={isDisabled}
			testId="miniplayer-play-from-beginning"
		/>
	);
};
